import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import equal from 'deep-equal';

import { isDocumentExists } from 'components/modules/MainAnketa/MainAnketa.helpers';
import { getDefaultValue, getDocTypeField, getDocTypeName } from './SecondDocumentForm.helpers';
import { NAMES, SECOND_DOCUMENT_TRUST, SYS_NAMES } from './SecondDocumentForm.constants';
import { KEY_COMPONENTS } from 'components/modules/MainAnketa/MainAnketa.constants';

export const useSecondDocumentFormPresenter = ({
  persona,
  validate,
  errors,
  isContentHidden,
  onChange,
}) => {
  const { service } = useParams();

  const { documents } = useSelector((state) => state.anketa);
  const { secondDocumentTypesList } = useSelector((state) => state.referenceBooks);

  const [localErrors, setLocalErrors] = useState({});
  const [secondDocFormValues, setSecondDocFormValues] = useState(null);
  const [secondDocValue, setSeconDocValue] = useState(null);
  const [secondDocType, setSecondDocType] = useState(null);
  const [updatedFromDoc, setUpdatedFromDoc] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const changeHandler = (formValue) => {
    setSecondDocFormValues((previousFormValues) => {
      const mergedFormValues = Array.isArray(formValue)
        ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
        : { ...previousFormValues, ...formValue };

      const docDataFieldName = getDocTypeName(mergedFormValues.currentSecondDocumentType);

      onChange({
        id: persona.id,
        key: KEY_COMPONENTS.SECOND_DOCUMENT,
        values: {
          ...getDefaultValue(),
          currentSecondDocumentTypeSysName: mergedFormValues.currentSecondDocumentType?.sysName,
          currentSecondDocumentType: mergedFormValues.currentSecondDocumentType,
          [docDataFieldName]: mergedFormValues[docDataFieldName],
        },
      });

      return mergedFormValues;
    });
  };

  const changeSerialNumber = useCallback(
    (formValue, mask) => {
      if (!secondDocFormValues) {
        return;
      }

      const normalizedValue = formValue.toUpperCase();
      const secondDocumentType = secondDocumentTypesList.find((doc) => doc.sysName === mask.name);

      setSecondDocType(secondDocumentType);
      setSeconDocValue(normalizedValue);

      const series4 = normalizedValue.slice(0, 4);
      const number4 = normalizedValue.slice(4, normalizedValue.length);
      const series2 = normalizedValue.slice(0, 2);
      const number2 = normalizedValue.slice(2, normalizedValue.length);
      const part1 = normalizedValue.slice(0, 3);
      const part2 = normalizedValue.slice(3, 6);
      const part3 = normalizedValue.slice(6, 9);
      const part4 = normalizedValue.slice(9, 11);

      changeHandler({
        ...getDefaultValue(),
        currentSecondDocumentTypeSysName: secondDocumentType?.sysName,
        currentSecondDocumentType: { ...secondDocumentType },
        [NAMES.DRIVER_LICENSE]: {
          ...secondDocFormValues.driverLicense,
          seriesNumber: normalizedValue,
          series: series4 || '',
          number: number4 || '',
        },
        [NAMES.INTERNATIONAL_PASSPORT]: {
          ...secondDocFormValues.internationalPassport,
          seriesNumber: normalizedValue,
          series: series2 || '',
          number: number2 || '',
        },
        [NAMES.MILITARY_ID]: {
          ...secondDocFormValues.militaryID,
          seriesNumber: normalizedValue,
          series: series2 || '',
          number: number2 || '',
        },
        [NAMES.TAX_REGISTRATION]: {
          ...secondDocFormValues.taxRegistration,
          inn: normalizedValue,
        },
        [NAMES.SNILS]: {
          ...secondDocFormValues.snils,
          number: `${part1 || ''}-${part2 || ''}-${part3 || ''} ${part4 || ''}`,
        },
      });
    },
    [secondDocFormValues, secondDocumentTypesList, changeHandler]
  );

  const changeHandlerSecondDoc = useCallback(
    (formValue, mask) => {
      if (updatedFromDoc) {
        setUpdatedFromDoc(false);
      } else {
        changeSerialNumber(formValue.SecDocInput, mask.masked.currentMask);
      }
    },
    [changeHandler, changeSerialNumber]
  );

  const changeHandlerPassportDate = useCallback(
    (formValue) => {
      const normalizedValue =
        formValue[NAMES.ISSUE_DATE]?.length === 2 ? '' : formValue[NAMES.ISSUE_DATE];

      changeHandler({
        [NAMES.INTERNATIONAL_PASSPORT]: {
          ...secondDocFormValues[NAMES.INTERNATIONAL_PASSPORT],
          [NAMES.ISSUE_DATE]: normalizedValue,
        },
      });
    },
    [changeHandler]
  );

  const changeHandlerDriverLicenseDate = useCallback(
    (formValue) => {
      const normalizedValue =
        formValue[NAMES.ISSUE_DATE]?.length === 2 ? '' : formValue[NAMES.ISSUE_DATE];

      changeHandler({
        [NAMES.DRIVER_LICENSE]: {
          ...secondDocFormValues[NAMES.DRIVER_LICENSE],
          [NAMES.ISSUE_DATE]: normalizedValue,
        },
      });
    },
    [changeHandler]
  );

  const validation = useCallback(
    (path) => {
      setLocalErrors((prev) => ({ ...prev, [path]: errors[path] || null }));
    },
    [errors]
  );

  const getError = useCallback(
    (path) => {
      const validationError = errors[path];
      const generalError = localErrors[path];

      if (!validate) {
        return generalError;
      }

      return validationError ?? generalError;
    },
    [validate, errors, localErrors]
  );

  const isSecondDocError = useMemo(() => {
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
      return false;
    }

    return Object.keys(errors).some((key) => key.includes(KEY_COMPONENTS.SECOND_DOCUMENT));
  }, [errors]);

  const getShowDate = useCallback(() => {
    return [SYS_NAMES.SECOND_DRIVER, SYS_NAMES.SECOND_FOREIGN_PASSPORT].includes(
      secondDocType?.sysName
    );
  }, [secondDocType]);

  const onHeaderClick = useCallback(() => {
    setShowMore((prev) => !prev);
  }, []);

  const getSecondDocHeader = useMemo(() => {
    if (!secondDocValue && !secondDocType?.value) {
      return 'Второй документ';
    }

    if (secondDocType?.value) {
      if (secondDocType.value === 'Свидетельство о регистрации в налоговом органе') {
        return 'ИНН';
      }
      return secondDocType.value;
    }

    return 'Второй документ';
  }, [secondDocType?.value, secondDocValue]);

  const shouldShowCheckIcon = useMemo(() => {
    return isDocumentExists(documents, SECOND_DOCUMENT_TRUST) && !isSecondDocError;
  }, [documents, isSecondDocError]);

  const shouldShowAlertIcon = useMemo(() => {
    return (
      !isDocumentExists(documents, SECOND_DOCUMENT_TRUST) || !secondDocValue || isSecondDocError
    );
  }, [documents, isSecondDocError, secondDocValue]);

  useEffect(() => {
    if (!persona || equal(secondDocFormValues, persona[KEY_COMPONENTS.SECOND_DOCUMENT])) {
      return;
    }

    setSecondDocFormValues({ ...getDefaultValue(), ...persona[KEY_COMPONENTS.SECOND_DOCUMENT] });
  }, [persona]);

  useEffect(() => {
    if (!secondDocFormValues) {
      return;
    }

    const [doc, number] = getDocTypeField(secondDocFormValues.currentSecondDocumentType).split('.');
    const currentSecondDocType = secondDocumentTypesList.find(
      (secondDoc) => secondDoc.sysName === secondDocFormValues.currentSecondDocumentType?.sysName
    );

    if (
      secondDocType &&
      secondDocFormValues.currentSecondDocumentType?.sysName !== secondDocType.sysName
    ) {
      setUpdatedFromDoc(true);
    }

    setSecondDocType(currentSecondDocType);
    setSeconDocValue(secondDocFormValues[doc][number].replaceAll('-', '').replaceAll(' ', ''));
  }, [secondDocFormValues]);

  useEffect(() => {
    if (validate) {
      return;
    }

    setLocalErrors(
      Object.keys(localErrors).reduce((obj, key) => ({ ...obj, [key]: errors[key] || null }), {})
    );
  }, [errors, validate]);

  useEffect(() => {
    if (!service) {
      return;
    }

    changeHandler({
      currentSecondDocumentType: secondDocumentTypesList.find(
        (secondDoc) => secondDoc.sysName === SYS_NAMES.SECOND_DRIVER
      ),
    });
  }, []);

  useEffect(() => {
    if (
      documents &&
      isDocumentExists(documents, SECOND_DOCUMENT_TRUST) &&
      secondDocFormValues &&
      errors
    ) {
      if (isSecondDocError) {
        setShowMore(true);
      }
    }
  }, [errors, secondDocFormValues]);

  useEffect(() => {
    if (showMore && isContentHidden) {
      setShowMore(false);
    }
  }, [showMore, isContentHidden]);

  return {
    showMore,
    secondDocType,
    secondDocValue,
    getSecondDocHeader,
    secondDocFormValues,
    shouldShowAlertIcon,
    shouldShowCheckIcon,
    getError,
    validation,
    getShowDate,
    onHeaderClick,
    changeHandlerSecondDoc,
    changeHandlerPassportDate,
    changeHandlerDriverLicenseDate,
  };
};
